import React, { memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { CardView } from 'design-system';
import { useAppSelector } from 'store/hooks';
import { getCompactNumber, formatAmount } from 'utils/formatting';
import SectionHeader from './SectionHeader';
import AboutInfoColumn from './AboutInfoColumn';
const toolTipInfo = {
    marketCap: {
        toolTipTitle: 'Market cap',
        toolTipDescription: 'A common data point to determine the overall value of a company. It’s calculated by multiplying the current share price by the total number of shares the company has issued and outstanding.',
    },
    peRatio: {
        toolTipTitle: 'P/E ratio',
        toolTipDescription: 'The Price to Earnings Ratio. It is calculated by dividing the share price by the Earnings per Share (EPS). It is a common data point for comparing the relative valuation of a company’s shares.',
    },
    eps: {
        toolTipTitle: 'EPS',
        toolTipDescription: 'A number that serves as an indicator of a company’s profitability. EPS is calculated as a company’s profit divided by the outstanding shares of its common stock.',
    },
    beta: {
        toolTipTitle: 'Beta (Volatility)',
        toolTipDescription: 'Beta is a measure of a stock’s volatility in relation to the overall market. A beta greater than 1.0 suggests that the stock is more volatile than the broader market, and a beta less than 1.0 indicates a stock with lower volatility.',
    },
    dividendRate: {
        toolTipTitle: 'Dividend rate',
        toolTipDescription: 'Dividend rate is the amount of cash an investor can expect to receive on an annual basis for holding 1 share in a company, assuming the dividend remains the same.\n\nDividends, set by the company’s board, are the distribution of some of the company’s earnings to its shareholders as a reward for putting their money into the company.\n\nDividend rate is calculated by adding up the dividends that were paid out to shareholders in the last 12 months.',
    },
    dividendYield: {
        toolTipTitle: 'Dividend Yield',
        toolTipDescription: 'The percentage of holdings in a company an investor can expect to receive in dividends each year. Dividends are a periodic distribution from a company to its shareholders. Not all stocks issue dividends.',
    },
};
const StockAboutMetricsSection = ({ pe, eps, beta, marketCap, dividendRate, dividendYield, }) => {
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const data = {
        eps: eps ? formatAmount(eps, 'USD', 2, scrambled) : undefined,
        pe: pe ? formatAmount(pe, undefined, 2, scrambled) : undefined,
        marketCap: marketCap ? getCompactNumber(marketCap, 2) : undefined,
        beta: formatAmount(beta, undefined, 2, scrambled),
        dividendRate: dividendRate ? formatAmount(dividendRate, 'USD', 2, scrambled) : undefined,
        dividendYield: dividendYield ? `${formatAmount(dividendYield, undefined, 2, scrambled)}%` : undefined,
    };
    return (<>
      <SectionHeader title="Metrics"/>
      <CardView noMarginTop noMarginBottom>
        <View style={styles.row}>
          <AboutInfoColumn title="Market Cap" info={data?.marketCap} {...toolTipInfo.marketCap}/>
          <AboutInfoColumn right title="P/E ratio" info={data?.pe} {...toolTipInfo.peRatio}/>
        </View>
        <View style={styles.row}>
          <AboutInfoColumn title="EPS" info={data?.eps} {...toolTipInfo.eps}/>
          <AboutInfoColumn right title="Beta" info={data.beta} {...toolTipInfo.beta}/>
        </View>
        <View style={styles.row}>
          <AboutInfoColumn title="Dividend rate" info={data.dividendRate} {...toolTipInfo.dividendRate}/>
          <AboutInfoColumn right title="Dividend yield" info={data.dividendYield} {...toolTipInfo.dividendYield}/>
        </View>
      </CardView>
    </>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
    },
});
export default memo(StockAboutMetricsSection);
